import { StringParam, useParam } from "@redotech/react-router-util/param";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { PickSessionStrategy } from "@redotech/redo-model/wms/schema/pick-session";
import {
  AdvancedTableFilter,
  decodeAdvancedFilters,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { getAdvancedFilterArray } from "@redotech/redo-web/table2";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFGAdvancedFilters } from "../fulfillment-groups/filters/get-fg-advanced-filters";
import { OriginLocationFilter } from "../fulfillment-groups/filters/origin-location-filter";
import { PickSessionExistsFilter } from "../fulfillment-groups/filters/pick-session-exists-filter";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { EnabledProvidersContext } from "../services/enabled-providers-service";
import { getTeamOMSUsers } from "../utils/get-team-oms-users";
import * as styles from "./create-pick-session-page.module.css";
import { PickSessionMetrics } from "./pick-session-metrics";
import { QuantityIncrementDecrement } from "./quantity-increment-decrement";

const DEFAULT_ORDER_QUANTITY = 8;

export const CreatePickSessionPage = memo(function CreatePickSessionPage() {
  const [locationId] = useParam(new StringParam("locationId", ""));
  const [strategy] = useParam(new StringParam("strategy", ""));
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const teamUsers = getTeamOMSUsers(team);
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const enabledProviders = useRequiredContext(EnabledProvidersContext);
  const navigate = useNavigate();

  const [orderQuantity, setOrderQuantity] = useState<number>(
    team?.settings?.outboundLabels?.wms?.pickSession?.defaultOrderCount ||
      DEFAULT_ORDER_QUANTITY,
  );

  const locationsLoad = useLoad(
    async (signal: AbortSignal) => {
      const { locations } = await rpcMerchantClient.getLocations(null, {
        signal,
      });

      return locations;
    },
    [rpcMerchantClient],
  );

  const locations = locationsLoad?.value || [];

  const advancedFilters = useMemo(
    (): AdvancedTableFilter[] => {
      const tableFilters = getFGAdvancedFilters({
        team,
        users: teamUsers,
        rpcClient,
        rpcMerchantClient,
        enabledProviders,
      });

      return [...tableFilters, PickSessionExistsFilter, OriginLocationFilter];
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [filtersFromParams] = useParam(
    new StringParam(
      "advancedFilters",
      getAdvancedFilterArray(undefined, advancedFilters),
    ),
  );

  const advancedFiltersApplied = useMemo(() => {
    return decodeAdvancedFilters(filtersFromParams, advancedFilters);
  }, [filtersFromParams, advancedFilters]);

  const availableOrders = useLoad(async () => {
    if (!advancedFiltersApplied.length) {
      setOrderQuantity(0);
      return 0;
    }

    const { total } = await rpcClient.getFulfillmentGroupSummaryCount({
      advancedFiltersData: advancedFiltersApplied.map((filter) => filter.data),
      merchantTimezone: team.ianaTimezone,
    });

    if (total < DEFAULT_ORDER_QUANTITY) {
      setOrderQuantity(total);
    }

    return total;
  }, [team, rpcClient, advancedFiltersApplied]);

  const [createLoad, create] = useTriggerLoad((signal) =>
    alertOnFailure("Pick session creation failed")(async () => {
      const validatedStrategy = Object.values(PickSessionStrategy).includes(
        strategy as PickSessionStrategy,
      )
        ? (strategy as PickSessionStrategy)
        : PickSessionStrategy.SINGLE_ITEM;

      const result = await rpcClient.createPickSession(
        {
          numberOfGroups: orderQuantity,
          searchInput: {
            advancedFiltersData: advancedFiltersApplied.map(
              (filter) => filter.data,
            ),
          },
          seedGroupId: null,
          locationId,
          strategy: validatedStrategy,
        },
        { signal },
      );

      if (result.pickSessionId) {
        navigate(`../pick-sessions/${result.pickSessionId}`);
      }
    }),
  );

  if (!locationsLoad?.value) {
    return null;
  }

  if (!locationId || !strategy) {
    return (
      <Flex align="center" dir="column" gap="xl" p="3xl" w="full">
        <Text fontSize="lg" fontWeight="semibold" textColor="primary">
          Location required
        </Text>
        <Text fontSize="md" textAlign="center" textColor="secondary">
          Missing required information, locationId or strategy.
        </Text>
        <RedoButton
          hierarchy="primary"
          onClick={() => navigate(-1)}
          size="lg"
          text="Go Back"
          theme="normal"
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="lg" p="lg" w="full">
      <Flex justify="flex-start" w="full">
        <RedoButton
          hierarchy="secondary"
          IconLeading={ArrowLeftIcon}
          onClick={() => navigate(`../pick-sessions?locationId=${locationId}`)}
          size="sm"
          text="Back to Dashboard"
          theme="normal"
        />
      </Flex>
      <PickSessionMetrics locationId={locationId} locations={locations} />

      <Card p="3xl" radius="md" w="full">
        <Flex align="center" dir="column" justify="center">
          <Text fontSize="lg" fontWeight="semibold" textAlign="center">
            How many orders do you want to fulfill?
          </Text>

          <QuantityIncrementDecrement
            max={Math.min(99, availableOrders?.value || 99)}
            min={0}
            setValue={setOrderQuantity}
            title={`Out of ${availableOrders?.value || 0} orders`}
            value={orderQuantity}
          />

          <div style={{ width: "100%" }}>
            <RedoButton
              buttonClassName={styles.startPickingButton}
              disabled={!availableOrders?.value || orderQuantity < 1}
              hierarchy="primary"
              IconLeading={CheckCircleIcon}
              onClick={() => {
                create();
              }}
              pending={createLoad.pending}
              size="lg"
              text="Start Picking Session"
              theme="normal"
            />
          </div>
        </Flex>
      </Card>
    </Flex>
  );
});
